import type { Sort } from "./table-view.ts"

/*
 * The rules by which a table's search and facets narrow its rows and count its facets' values, and
 * which rows it pins first, kept apart from the component so they can be checked without a browser.
 */

/** A facet's values for one row: what the row can be filtered by, each by its `value`. */
export type FacetOf<Row, Value extends { value: string }> = (row: Row) => readonly Value[]

/**
 * The rows a view leaves, whether it narrows them at all, and each facet's values with a count.
 *
 * A row passes the search when its text holds the query, in any case; a blank query passes every
 * row, and a row with no text passes only a blank one. It passes a facet when nothing is chosen
 * there or the row has any chosen value, and the view when it passes the search and every facet.
 *
 * A facet counts the rows its own choice would leave, among those the search and every other facet
 * leave: a value's count is the rows passing everything but that facet that carry the value. Its
 * values are every value any row has, matching or not, so a choice the other filters have
 * narrowed to nothing stays on offer, at 0, to be unchosen.
 */
export function filterRows<Row, Id extends string, Value extends { value: string }>({
  rows,
  facets,
  search,
  matches,
  filters,
  query,
}: {
  rows: readonly Row[]
  facets: ReadonlyMap<Id, FacetOf<Row, Value>>
  search?: (row: Row) => string | undefined
  /** A table's own rule for what the search finds, in place of `search`'s text. */
  matches?: (row: Row, query: string) => boolean
  filters: Partial<Record<Id, readonly string[]>>
  query: string
}) {
  const sought = query.trim().toLowerCase()
  const chosenIn = (id: Id) => filters[id] ?? []
  const found = (row: Row) =>
    !sought ||
    (matches ? matches(row, query) : (search?.(row) ?? "").toLowerCase().includes(sought))
  const passes = (row: Row, except?: Id) =>
    found(row) &&
    [...facets].every(([id, facet]) => {
      const chosen = chosenIn(id)
      return (
        id === except ||
        chosen.length === 0 ||
        facet(row).some(({ value }) => chosen.includes(value))
      )
    })

  const counts = new Map(
    [...facets].map(([id, facet]) => {
      const values = new Map(rows.flatMap(facet).map((value) => [value.value, value]))
      // Each row once per value it carries, however many times its facet names it.
      const carried = rows
        .filter((row) => passes(row, id))
        .flatMap((row) => [...new Set(facet(row).map(({ value }) => value))])
      return [
        id,
        [...values.values()].map((value) => ({
          value,
          count: carried.filter((each) => each === value.value).length,
        })),
      ] as const
    }),
  )

  return {
    matching: rows.filter((row) => passes(row)),
    filtered: sought !== "" || [...facets.keys()].some((id) => chosenIn(id).length > 0),
    counts,
  }
}

/**
 * `rows` with those `pinned` picks first, then the rest, each kept in the order it came in: a
 * table's pinned rows lead whatever it is sorted by. The list passed in is left as it was.
 */
export function pinnedFirst<Row>(rows: readonly Row[], pinned: (row: Row) => boolean) {
  return [...rows.filter(pinned), ...rows.filter((row) => !pinned(row))]
}

/**
 * Where a column's cell goes when the table's rows stack, on `compact`: the row's `title`, read
 * first on line one; `trailing`, at the end of line one; `subtitle`, `meta` or `labelled` on line
 * two, `labelled` written after its header ("Runs 12"); or `none`, left out.
 */
export type Stack = "title" | "subtitle" | "meta" | "labelled" | "trailing" | "none"

/**
 * A table's columns as a stacked row lays them out: its one `title`, the `trailing` cells after it
 * on line one, and line two's cells, the `subtitle` ones first, then `meta`, then `labelled`, each
 * group in the columns' order. A `none` column is in neither line. A row needs exactly one title to
 * read first, so a table with none or several is refused, naming them.
 */
export function stackOf<Column extends { id: string; stack: Stack }>(columns: readonly Column[]) {
  const [title, ...others] = columns.filter(({ stack }) => stack === "title")
  if (!title || others.length > 0)
    throw new Error(
      `A table's rows stack under exactly one title column, not ${
        title ? [title, ...others].map(({ id }) => id).join(", ") : "none"
      }.`,
    )
  const placed = (stack: Stack) => columns.filter((column) => column.stack === stack)
  return {
    title,
    trailing: placed("trailing"),
    second: [...placed("subtitle"), ...placed("meta"), ...placed("labelled")],
  }
}

/**
 * The orders a table offers in its Order by: each sortable column, by its `orderLabel` or else its
 * header, and in the direction its first click sorts. The table's default order leads, then the
 * rest in the columns' order. Which way the one in force runs is chosen apart (`orderChosen`).
 */
export function ordersOf<Id extends string>(
  columns: readonly {
    id: Id
    header: string
    orderLabel?: string
    sortBy?: unknown
    firstDirection?: "asc" | "desc"
  }[],
  first: Id,
) {
  const sortable = columns.filter((column) => column.sortBy)
  return pinnedFirst(sortable, ({ id }) => id === first).map(
    ({ id, header, orderLabel, firstDirection }) => ({
      value: id,
      label: orderLabel ?? header,
      direction: firstDirection === "desc" ? ("descending" as const) : ("ascending" as const),
    }),
  )
}

/** A choice in Order by: one of its orders, by its column, or which way the one in force runs. */
export type OrderChoice = { column: string } | { direction: Sort["direction"] }

/**
 * The sort a choice in Order by writes, from the sort in force: another column sorts the way its
 * head sorts first, as a click on that head would; the column in force, chosen again, keeps its
 * direction; and a direction reverses the column in force or keeps it. A column the table doesn't
 * offer changes nothing.
 */
export function orderChosen<Id extends string>(
  orders: readonly { value: Id; direction: Sort["direction"] }[],
  current: Sort<Id>,
  choice: OrderChoice,
): Sort<Id> {
  if ("direction" in choice) return { column: current.column, direction: choice.direction }
  if (choice.column === current.column) return current
  const order = orders.find(({ value }) => value === choice.column)
  return order ? { column: order.value, direction: order.direction } : current
}

/**
 * Where a table's scroller goes as its page turns, so the new page is read from its first row
 * rather than where the last one was left. `scrollTop` is where the scroller stands, `top` how far
 * the table's top is below the scroller's own (less than 0 once scrolled past), and `covered` how
 * much of the scroller's top the chrome stuck above the table hides. A table scrolled past comes
 * back to rest its top just under that chrome; one whose top shows already stays (undefined).
 */
export function turnedPageScroll({
  scrollTop,
  top,
  covered,
}: {
  scrollTop: number
  top: number
  covered: number
}) {
  const hidden = top - covered
  return hidden < 0 ? Math.max(0, scrollTop + hidden) : undefined
}

/*
 * Choosing rows, for what a page does to several at once: the rows chosen are their ids, held by the
 * page and passed in, and the table's checkboxes give back the next choice.
 */

/** Whether none, some or every one of the rows `shown` is `chosen`: a select-all's state. */
export function chosenOf(chosen: readonly string[], shown: readonly string[]) {
  const picked = new Set(chosen)
  const count = shown.filter((id) => picked.has(id)).length
  return count === 0 ? "none" : count === shown.length ? "all" : "some"
}

/**
 * The choice after a select-all is pressed: every row `shown`, or, once every one is chosen, none of
 * them. A chosen row the view hides is left as it was.
 */
export function chooseAll(chosen: readonly string[], shown: readonly string[]) {
  if (chosenOf(chosen, shown) === "all") {
    const unchosen = new Set(shown)
    return chosen.filter((id) => !unchosen.has(id))
  }
  const picked = new Set(chosen)
  return [...chosen, ...shown.filter((id) => !picked.has(id))]
}

/** The choice after row `id`'s checkbox is pressed: chosen, or unchosen when it was. */
export function toggleChosen(chosen: readonly string[], id: string) {
  return chosen.includes(id) ? chosen.filter((each) => each !== id) : [...chosen, id]
}

/**
 * The chosen rows the view still shows, in the order chosen, so a search or a filter that hides a
 * row unchooses it, and what the page acts on is always what the Member sees. Every one still shown,
 * it is `chosen` itself, so a page holding it is told of no change.
 */
export function chosenShown(chosen: readonly string[], shown: readonly string[]) {
  const showing = new Set(shown)
  return chosen.every((id) => showing.has(id)) ? chosen : chosen.filter((id) => showing.has(id))
}
